import { useState, useEffect, useRef, useContext } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftBadge from "components/SoftBadge";
import {
  getTicketById,
  getTicketMessages,
  createTicketMessage,
  downloadAttachment,
  getTicketAttachments,
  uploadAttachment,
  updateTicket
} from "services/ticketService";
import { AuthContext } from "context";

// Styled Input für Datei-Upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function TicketDetail({ ticketId, onClose }) {
  const { user } = useContext(AuthContext);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messageEndRef = useRef(null);
  
  // Automatisches Scrollen zum neuesten Nachricht
  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const loadTicketData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Stellen Sie sicher, dass die ID ein gültiges Format hat
        const cleanTicketId = String(ticketId).replace('T-', '');

        const [ticketData, messagesData, attachmentsData] = await Promise.all([
          getTicketById(cleanTicketId),
          getTicketMessages(cleanTicketId),
          getTicketAttachments(cleanTicketId)
        ]);

        setTicket(ticketData);
        setMessages(messagesData || []);
        setAttachments(attachmentsData || []);
      } catch (error) {
        console.error("Fehler beim Laden der Ticket-Daten:", error);
        setError(`Fehler beim Laden: ${error.message || 'Unbekannter Fehler'}`);
      } finally {
        setLoading(false);
      }
    };

    if (ticketId) {
      loadTicketData();
    }
  }, [ticketId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && newFiles.length === 0) return;
    
    try {
      setUploading(true);

      // Stellen Sie sicher, dass die ID ein gültiges Format hat
      const cleanTicketId = String(ticketId).replace('T-', '');

      // Nachricht senden
      if (newMessage.trim()) {
        await createTicketMessage({
          ticketId: cleanTicketId,
          message: newMessage,
          senderType: 1, // 1 für Benutzer, 2 für Support-Mitarbeiter
          senderId: user?.id
        });
      }
      
      // Dateien hochladen
      if (newFiles.length > 0) {
        for (const file of newFiles) {
          await uploadAttachment(cleanTicketId, file);
        }
      }
      
      // Aktualisiere Daten
      const [updatedMessages, updatedAttachments] = await Promise.all([
        getTicketMessages(cleanTicketId),
        getTicketAttachments(cleanTicketId)
      ]);
      
      setMessages(updatedMessages || []);
      setAttachments(updatedAttachments || []);
      setNewMessage("");
      setNewFiles([]);
    } catch (error) {
      console.error("Fehler beim Senden der Nachricht:", error);
      setError(`Fehler beim Senden: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setNewFiles(Array.from(e.target.files));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const blob = await downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Fehler beim Herunterladen der Datei:", error);
      setError(`Fehler beim Herunterladen: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  const handleCloseTicket = async () => {
    try {
      // Stellen Sie sicher, dass die ID ein gültiges Format hat
      const cleanTicketId = String(ticketId).replace('T-', '');
      
      // Senden Sie den aktuellen Betreff und andere erforderliche Felder mit
      await updateTicket(cleanTicketId, { 
        status: "Gelöst",
        subject: ticket.subject,
        description: ticket.description,
        priority: ticket.priority
      });
      
      setTicket({ ...ticket, status: "Gelöst" });
    } catch (error) {
      console.error("Fehler beim Schließen des Tickets:", error);
      setError(`Fehler beim Schließen: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  // Lade-Anzeige, wenn Daten noch geladen werden
  if (loading) {
    return (
      <Card>
        <SoftBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress color="info" />
          <SoftTypography variant="button" color="text" ml={2}>
            Ticket-Daten werden geladen...
          </SoftTypography>
        </SoftBox>
      </Card>
    );
  }

  // Fehleranzeige
  if (error) {
    return (
      <Card>
        <SoftBox p={3}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <SoftTypography variant="h5" fontWeight="medium" color="error">
              Fehler beim Laden des Tickets
            </SoftTypography>
            <SoftButton variant="outlined" color="secondary" onClick={onClose}>
              Zurück
            </SoftButton>
          </SoftBox>
          <SoftBox p={2} border="1px solid" borderColor="error.light" borderRadius="lg" bgcolor="error.light">
            <SoftTypography variant="body2">{error}</SoftTypography>
          </SoftBox>
          <SoftButton variant="contained" color="info" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
            Erneut versuchen
          </SoftButton>
        </SoftBox>
      </Card>
    );
  }

  // Wenn kein Ticket geladen wurde
  if (!ticket) {
    return (
      <Card>
        <SoftBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <SoftTypography variant="button" color="text">
            Ticket wurde nicht gefunden. <SoftButton color="info" onClick={onClose}>Zurück</SoftButton>
          </SoftTypography>
        </SoftBox>
      </Card>
    );
  }
  
  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    switch (status) {
      case "Gelöst": return "success";
      case "In Bearbeitung": return "warning";
      case "Offen": return "info";
      default: return "error";
    }
  };

  return (
    <Card>
      <SoftBox p={3}>
        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftBox>
            <SoftTypography variant="h5" fontWeight="medium">
              {ticket.subject}
            </SoftTypography>
            <SoftBox display="flex" gap={1} alignItems="center" mt={1}>
              <SoftBadge variant="contained" color={getStatusColor(ticket.status)}>
                {ticket.status}
              </SoftBadge>
              <SoftTypography variant="caption">
                Priorität: {ticket.priority} | Ticket-Nr.: T-{ticket.id}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            {ticket.status !== "Gelöst" && (
              <SoftButton 
                variant="outlined" 
                color="success"
                onClick={handleCloseTicket}
                startIcon={<Icon>check_circle</Icon>}
              >
                Fall schließen
              </SoftButton>
            )}
            <SoftButton variant="outlined" color="secondary" onClick={onClose}>
              Zurück
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Chat-Bereich */}
            <SoftBox mb={2}>
              {/* Initiale Ticket-Nachricht */}
              <SoftBox 
                mb={3} 
                p={2} 
                bgcolor="grey.100" 
                borderRadius="lg" 
                border="1px solid"
                borderColor="grey.300"
              >
                <SoftBox display="flex" alignItems="center" mb={1}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, mr: 1 }}>
                    {ticket.webuser?.username?.[0]?.toUpperCase() || "U"}
                  </Avatar>
                  <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium">
                      {ticket.webuser?.username || "Benutzer"}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" ml={1}>
                      {new Date(ticket.createdAt).toLocaleString('de-DE')}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftTypography variant="body2">{ticket.description}</SoftTypography>
              </SoftBox>
              
              {/* Chat-Nachrichten */}
              <SoftBox 
                sx={{ 
                  height: "400px", 
                  overflowY: "auto", 
                  px: 1, 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: 2 
                }}
              >
                {messages && messages.length > 0 ? (
                  messages.map((message) => (
                    <SoftBox
                      key={message.id}
                      alignSelf={message.senderType === 1 ? "flex-end" : "flex-start"}
                      maxWidth="70%"
                    >
                      <SoftBox 
                        p={2}
                        bgcolor={message.senderType === 1 ? "info.main" : "grey.200"}
                        color={message.senderType === 1 ? "white" : "inherit"}
                        borderRadius="lg"
                        sx={{
                          borderBottomRightRadius: message.senderType === 1 ? 0 : undefined,
                          borderBottomLeftRadius: message.senderType === 2 ? 0 : undefined
                        }}
                      >
                        <SoftTypography variant="body2">
                          {message.message}
                        </SoftTypography>
                        <SoftTypography 
                          variant="caption" 
                          color={message.senderType === 1 ? "white" : "text.secondary"}
                          textAlign="right"
                          display="block"
                          mt={0.5}
                        >
                          {new Date(message.createdAt).toLocaleTimeString('de-DE')}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ))
                ) : (
                  <SoftBox display="flex" justifyContent="center" alignItems="center" height="100%">
                    <SoftTypography variant="button" color="text">
                      Keine weiteren Nachrichten vorhanden
                    </SoftTypography>
                  </SoftBox>
                )}
                <div ref={messageEndRef} />
              </SoftBox>
              
              {/* Anhänge anzeigen */}
              {attachments && attachments.length > 0 && (
                <SoftBox mt={2} mb={2}>
                  <SoftTypography variant="button" fontWeight="medium" mb={1}>
                    Anhänge ({attachments.length})
                  </SoftTypography>
                  <SoftBox 
                    display="flex" 
                    gap={1} 
                    sx={{ 
                      flexWrap: "wrap", 
                      maxHeight: "100px", 
                      overflowY: "auto", 
                      p: 1, 
                      border: "1px solid",
                      borderColor: "grey.300", 
                      borderRadius: 1
                    }}
                  >
                    {attachments.map((attachment) => (
                      <SoftButton
                        key={attachment.id}
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleDownload(attachment.id, attachment.filename)}
                        startIcon={<Icon>attachment</Icon>}
                        sx={{ mb: 1 }}
                      >
                        {attachment.filename.length > 15 ? 
                          `${attachment.filename.substring(0, 15)}...` : 
                          attachment.filename
                        }
                      </SoftButton>
                    ))}
                  </SoftBox>
                </SoftBox>
              )}
              
              {/* Neue Nachricht eingeben */}
              {ticket.status !== "Gelöst" && (
                <SoftBox display="flex" flexDirection="column" gap={2} mt={2}>
                  <SoftInput
                    multiline
                    rows={3}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ihre Nachricht..."
                    fullWidth
                  />
                  
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftBox display="flex" gap={1} alignItems="center">
                      <Button
                        component="label"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon>attach_file</Icon>}
                      >
                        Datei anhängen
                        <VisuallyHiddenInput type="file" onChange={handleFileChange} />
                      </Button>
                      {newFiles.length > 0 && (
                        <SoftTypography variant="caption">
                          {newFiles.length} Datei{newFiles.length > 1 ? 'en' : ''} ausgewählt
                        </SoftTypography>
                      )}
                    </SoftBox>
                    
                    <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={handleSendMessage}
                      disabled={(!newMessage.trim() && newFiles.length === 0) || uploading}
                      endIcon={<Icon>send</Icon>}
                    >
                      {uploading ? 'Wird gesendet...' : 'Senden'}
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
}

TicketDetail.propTypes = {
  ticketId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TicketDetail;